import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Plus, Check } from "lucide-react";
import { PRODUCTS, IN_THE_BOX } from "../data/products";
import { QuickCheckout } from "../components/QuickCheckout";
import { useCart } from "../context/CartContext";
import { BagButton } from "../components/BagButton";

const SpecBlock = ({ title, children, testid }) => (
  <div className="border-t border-white/10 py-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{title}</p>
    {children}
  </div>
);

export default function ProductPage() {
  const { id } = useParams();
  const index = PRODUCTS.findIndex((p) => p.id === id);
  const product = PRODUCTS[index];
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const [showPay, setShowPay] = useState(false);

  if (!product) return <Navigate to="/" replace />;
  const next = PRODUCTS[(index + 1) % PRODUCTS.length];

  const addToBag = () => {
    add(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div className="min-h-screen bg-ink" data-testid={`product-page-${product.id}`}>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-12 py-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="product-nav-logo-link">
            <span className="block h-9 w-9 overflow-hidden rounded-full border border-gold/40">
              <img
                src="/assets/logo.jpg"
                alt="RayStance monogram"
                className="h-full w-full object-cover object-top scale-[1.6] origin-top"
              />
            </span>
            <span className="font-serif text-lg tracking-[0.25em] text-white group-hover:text-gold transition-colors duration-500">
              RAYSTANCE
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/collection"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash hover:text-gold transition-colors duration-500"
              data-testid="product-back-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Collection
            </Link>
            <BagButton />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border border-white/5"
            >
              <img
                src={product.image}
                alt={product.alt}
                className="w-full max-h-[80vh] object-cover"
                data-testid="product-main-image"
              />
            </motion.div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden border border-white/5 h-56 md:h-72"
                data-testid="product-detail-hardware"
              >
                <img
                  src={product.image}
                  alt={`${product.name} hardware close-up`}
                  className="h-full w-full object-cover object-right-top scale-[2]"
                />
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] text-white/80 bg-ink/60 px-2 py-1">
                  Detail — Hardware
                </span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden border border-white/5 h-56 md:h-72"
                data-testid="product-detail-lens"
              >
                <img
                  src={product.image}
                  alt={`${product.name} lens close-up`}
                  className="h-full w-full object-cover object-left-bottom scale-[2]"
                />
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] text-white/80 bg-ink/60 px-2 py-1">
                  Detail — Lens
                </span>
              </motion.div>
            </div>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="md:sticky md:top-28"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{product.edition}</p>
              <h1 className="font-serif text-6xl md:text-7xl font-light text-white leading-none" data-testid="product-page-name">
                {product.name}
              </h1>
              <p className="mt-4 font-serif italic text-xl text-champagne">{product.tagline}</p>
              <p className="mt-6 text-sm md:text-base text-ash leading-relaxed">{product.description}</p>

              <div className="mt-8 flex flex-wrap items-center gap-6">
                <div className="flex items-baseline gap-3">
                  <p className="font-serif text-4xl text-champagne" data-testid="product-page-price">
                    ${product.price}
                  </p>
                  <p className="text-base text-ash/70 line-through" data-testid="product-page-compare-at">
                    ${product.compareAt}
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/40 rounded-full px-3 py-1">
                    Launch Offer
                  </span>
                </div>
                <p className="basis-full text-[11px] uppercase tracking-[0.2em] text-ash/60" data-testid="product-page-launch-ends">
                  Launch pricing ends soon
                </p>
                <button
                  onClick={() => setShowPay(true)}
                  className="group relative inline-flex items-center gap-2 rounded-full border border-gold/60 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-gold overflow-hidden transition-colors duration-500 hover:text-ink"
                  data-testid="product-page-buy-button"
                >
                  <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                  <span className="relative">Acquire</span>
                  <ArrowUpRight className="relative h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <button
                  onClick={addToBag}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
                  data-testid="product-page-add-bag"
                >
                  {added ? (
                    <>
                      <Check className="h-4 w-4 text-gold" /> Added
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" /> Add to Bag
                    </>
                  )}
                </button>
              </div>

              <div className="mt-10">
                <SpecBlock title="Materials" testid="product-materials">
                  <ul className="space-y-2 text-sm text-ash">
                    {product.materials.map((m) => (
                      <li key={m} className="flex gap-3">
                        <span className="text-gold/60">—</span>
                        {m}
                      </li>
                    ))}
                  </ul>
                </SpecBlock>
                <SpecBlock title="Sizing" testid="product-sizing">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-white font-medium">{product.sizing.lens}</p>
                      <p className="text-ash text-xs mt-1 uppercase tracking-wider">Lens</p>
                    </div>
                    <div>
                      <p className="text-white font-medium">{product.sizing.bridge}</p>
                      <p className="text-ash text-xs mt-1 uppercase tracking-wider">Bridge</p>
                    </div>
                    <div>
                      <p className="text-white font-medium">{product.sizing.temple}</p>
                      <p className="text-ash text-xs mt-1 uppercase tracking-wider">Temple</p>
                    </div>
                  </div>
                </SpecBlock>
                <SpecBlock title="In the Box" testid="product-in-the-box">
                  <ul className="space-y-2 text-sm text-ash">
                    {IN_THE_BOX.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="text-gold/60">—</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </SpecBlock>
              </div>
            </motion.div>
          </div>
        </div>

        <Link
          to={`/product/${next.id}`}
          className="group mt-24 md:mt-32 flex items-end justify-between border-t border-white/10 pt-10"
          data-testid="product-next-link"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-ash">Next frame</p>
            <p className="mt-3 font-serif font-light text-4xl md:text-6xl text-white group-hover:text-gold transition-colors duration-500">
              {next.edition} — <span className="italic">{next.name}</span>
            </p>
          </div>
          <ArrowRight className="h-8 w-8 text-gold transition-transform duration-500 group-hover:translate-x-2" />
        </Link>
        <QuickCheckout
          items={showPay ? [{ lookup_key: product.lookupKey, quantity: 1 }] : null}
          title={`${product.name} — $${product.price}`}
          onClose={() => setShowPay(false)}
        />
      </main>
    </div>
  );
}
